(function () {
  'use strict';

  /*
    HIDEit live "Economy" shipping rate (v4)
    Fills every [data-live-shipping-rate] element (e.g. the ||rate|| in the
    free shipping message) with the store's Economy rate.

    v4 (2026-10-20): "Calculating..." kept flashing every second or two. Cause: when
    the rate lookup found no rate named "Economy" (or the lookup failed) the script
    kept no answer, so every small redraw of the drawer (e.g. the Shipping
    Protection price refreshing) made it show "Calculating..." and look up the
    rate again, over and over. Also, the lookup returns what shipping costs for
    the cart AS IT IS NOW - under $100 that is not the $5 offer the message is
    talking about ("Spend $9 more for $5 US Economy Shipping"). So by default
    the rate is now the fixed $5 offer (REWARD_RATE below): shown instantly,
    never "Calculating...", and only re-checked when the cart really changes.
    The live lookup is still available (LIVE_LOOKUP = true), and now remembers
    every answer per cart - including "no Economy rate" and failures - so it
    can never loop.

    v3: also catches a raw "||rate||" left in the page. The theme's own cart JS
    rewrites the free shipping message after every cart change and puts the raw
    placeholder back; this script turns it into the live rate right away.

    v2 fixes the "Calculating..." loop:
    - Our own text updates no longer trigger the observer (they used to re-run it forever).
    - The rate is only fetched when the cart actually changes (total / items / weight).
    - The last known rate is shown instantly after the drawer re-renders, so no flicker.
    - "Calculating..." only shows the first time, when no rate is known yet.

    Debug: add ?shipdebug=1 to the URL and open the browser Console.
  */

  var DEBUG = /[?&]shipdebug=1/.test(location.search);
  function log() { if (DEBUG) console.log.apply(console, ['[HIDEit ship-estimate]'].concat([].slice.call(arguments))); }

  // Default US ZIP used only to estimate a representative "Economy" rate
  // before a real shipping address is known (cart/drawer, pre-checkout).
  // Wichita, KS - roughly continental-US-central; swap if a better default exists.
  var DEFAULT_ZIP = '67202';
  var TARGET = '[data-live-shipping-rate]';
  var PLACEHOLDER = '||rate||';
  var FALLBACK_RATE = '$5';
  var REWARD_RATE = '$5';      // the "$5 US Economy Shipping" offer shown in the message
  var LIVE_LOOKUP = false;     // true = ask Shopify for the live Economy rate instead

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var inFlight = null;
  var lastSignature = null;   // cart state the current rate belongs to
  var lastRateText = REWARD_RATE; // shown immediately, never "Calculating..."
  var writing = false;        // true while this script changes the text itself

  function formatMoney(cents) {
    if (window.theme && typeof window.theme.formatMoney === 'function' && window.theme.moneyFormat) {
      return window.theme.formatMoney(cents, window.theme.moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function targets() {
    return document.querySelectorAll(TARGET);
  }

  /* Turn any raw "||rate||" text into a live rate element */
  function hydratePlaceholders() {
    if (!document.body || document.body.textContent.indexOf(PLACEHOLDER) === -1) return;
    var walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        if (node.nodeValue.indexOf(PLACEHOLDER) === -1) return NodeFilter.FILTER_SKIP;
        var parent = node.parentNode;
        if (!parent || /^(SCRIPT|STYLE|TEXTAREA|TEMPLATE)$/.test(parent.nodeName)) return NodeFilter.FILTER_SKIP;
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    var found = [];
    while (walker.nextNode()) found.push(walker.currentNode);
    if (!found.length) return;

    writing = true;
    found.forEach(function (node) {
      var parts = node.nodeValue.split(PLACEHOLDER);
      var frag = document.createDocumentFragment();
      parts.forEach(function (part, i) {
        if (part) frag.appendChild(document.createTextNode(part));
        if (i < parts.length - 1) {
          var span = document.createElement('span');
          span.setAttribute('data-live-shipping-rate', '');
          span.dataset.fallbackText = FALLBACK_RATE;
          span.textContent = lastRateText || FALLBACK_RATE;
          frag.appendChild(span);
        }
      });
      node.parentNode.replaceChild(frag, node);
    });
    if (observer) observer.takeRecords();
    writing = false;
    log('replaced', found.length, 'raw ||rate|| placeholder(s)');
  }

  /* Change text without waking our own observer */
  function writeAll(getText) {
    writing = true;
    targets().forEach(function (el) {
      if (!el.dataset.fallbackText) el.dataset.fallbackText = el.textContent;
      var text = getText(el);
      if (el.textContent !== text) el.textContent = text;
    });
    if (observer) observer.takeRecords();
    writing = false;
  }

  function setFinal(text) {
    writeAll(function () { return text; });
  }

  /* Re-apply the known rate to freshly re-rendered elements (instant, no fetch) */
  function applyCached() {
    if (lastRateText) setFinal(lastRateText);
  }

  function cartSignature() {
    return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        return [cart.item_count, cart.total_price, cart.total_weight, cart.currency].join('|');
      });
  }

  function refresh() {
    hydratePlaceholders();
    if (targets().length === 0) return;
    if (!LIVE_LOOKUP) { applyCached(); return; }
    if (inFlight) return inFlight;

    inFlight = cartSignature()
      .then(function (signature) {
        if (signature === lastSignature) {
          log('cart unchanged, reusing', lastRateText);
          applyCached();
          return;
        }

        applyCached();        // keep showing the current rate while checking; never "Calculating..."
        lastSignature = signature; // remember this cart even if the lookup fails, so it can't loop

        var qs = 'shipping_address%5Bzip%5D=' + DEFAULT_ZIP + '&shipping_address%5Bcountry%5D=US';
        return fetch(root + 'cart/shipping_rates.json?' + qs, { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (data) {
            var rates = (data && data.shipping_rates) || [];
            var economy = rates.filter(function (r) { return /economy/i.test(r.name); })[0];
            lastSignature = signature;
            if (economy) {
              lastRateText = formatMoney(Math.round(parseFloat(economy.price) * 100));
              log('economy rate found', lastRateText);
              setFinal(lastRateText);
            } else {
              log('no Economy-named rate found, keeping', lastRateText);
              applyCached();
            }
          });
      })
      .catch(function (err) {
        log('rate fetch failed, keeping', lastRateText, err);
        applyCached();
      })
      .finally(function () {
        inFlight = null;
      });
    return inFlight;
  }

  var debounceTimer = null;
  function scheduleRefetch() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(refresh, 400);
  }

  /* True if a mutation is only our own rate text changing */
  function isOwnChange(mutation) {
    var node = mutation.target;
    if (node && node.nodeType === 3) node = node.parentNode;
    return !!(node && node.closest && node.closest(TARGET));
  }

  var observer = new MutationObserver(function (mutations) {
    if (writing) return;
    var relevant = mutations.some(function (m) { return !isOwnChange(m); });
    if (!relevant) return;
    hydratePlaceholders(); // theme put the raw ||rate|| back: swap it instantly
    applyCached();       // drawer re-rendered: show the known rate immediately
    scheduleRefetch();   // then check (cheaply) whether the cart changed
  });

  document.addEventListener('theme:cart:refresh', scheduleRefetch);
  document.addEventListener('cart:refresh', scheduleRefetch);

  var started = false;
  function start() {
    if (started) return;
    started = true;
    ['[data-api-cart-price]', '#cartItemsHolder', '.cart-drawer'].forEach(function (sel) {
      var el = document.querySelector(sel);
      if (el) observer.observe(el, { childList: true, subtree: true, characterData: true });
    });
    hydratePlaceholders();
    applyCached();       // replace any server-rendered "Calculating..." right away
    scheduleRefetch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();